<template>
  <div class="checkout-address-form">
    <h5 class="my-2">
      <b>{{ title }}</b>
    </h5>
    <div class="form-group row">
      <div class="col-md-12">
        <label for="customer-dropdown">Customer:</label>
        <div class="d-flex">
          <multiselect
            class="mr-2"
            v-model="selectedDBCustomer"
            :options="customers"
            :allow-empty="allowEmptyCustomer"
            :close-on-select="true"
            :clear-on-select="false"
            :placeholder="
              selectedDBCustomer?.customerNo
                ? customLabel(selectedDBCustomer)
                : 'Select a customer'
            "
            track-by="customerNo"
            label="customerNo"
            @input="handleCustomerSelect"
            :searchable="true"
            :custom-label="customLabel"
            :class="{
              'is-invalid': errors.customerNo,
              'invalid-dropwdown': errors.customerNo,
            }"
            :show-labels="false"
          >
            <template #option="{ option }">
              <p class="mb-1 font-weight-bold">
                {{ option.customerNo }} - {{ option.companyName }}
              </p>
              <div>
                <span
                  >{{ option.address?.City }},
                  {{
                    states.find((x) => x.AlphaCode === option.address?.State)
                      ?.Name
                  }} {{ option.address?.Zip }}</span
                >
              </div>
            </template>
          </multiselect>
          <b-button-group v-if="selectedDBCustomer !== null">
            <b-button
              variant="outline-danger"
              size="sm"
              title="Unselect Customer"
              @click="removeCustomer"
              v-if="allowEmptyCustomer"
            >
              <b-icon icon="x" aria-hidden="true"></b-icon>
            </b-button>
            <b-button
              class="d-flex align-items-center"
              variant="outline-primary"
              size="sm"
              title="Customer Details"
              target="_blank"
              :href="'/customer/' + selectedDBCustomer.customerNo"
            >
              <b-icon icon="person-fill" aria-hidden="true"></b-icon>
            </b-button>
          </b-button-group>
        </div>
        <div v-if="errors.customerNo" class="invalid-feedback">
          {{ errors.customerNo }}
        </div>
      </div>
    </div>
    <div v-if="selectedDBCustomer && customer.isCreditBlocked === true">
      <div class="bordered-container border-danger text-danger">
        {{ errors.creditBlocked }}
      </div>
    </div>
    <div v-if="selectedDBCustomer">
      <label class="floating-label">Address: </label>
      <div class="bordered-container">
        <div>
          <b>{{ customer.companyName }} - {{ customer.customerNo }}</b>
        </div>
        <div>
          <span
            >{{ customer.address?.Address1 }}
            {{ customer.address?.Address2 }}</span
          >
        </div>
        <div>
          <span
            >{{ customer.address?.City }},
            {{
              states.find((x) => x.AlphaCode === customer.address?.State)?.Name
            }} {{ customer.address?.Zip }}</span
          >
        </div>
        <div>
          <span>{{
            countries.find((x) => x.AlphaCode === customer.address?.Country)
              ?.Name
          }}</span>
        </div>
        <div>
          <span>Tel: {{ customer.address?.Phone || 'N/A' }}</span>
        </div>
      </div>
    </div>
    <div v-if="selectedDBCustomer && customer.paymentTerm && isBillTo">
      <label class="floating-label">Payment Term: </label>
      <div class="bordered-container">{{ customer.paymentTerm }}</div>
    </div>
    <div class="form-group row" v-if="showPOnumber">
      <div class="col-md-12">
        <label for="po-number">PO Number</label>
        <input
          type="text"
          id="po-number"
          class="form-control"
          v-model.trim="customer.poNumber"
          autocomplete="off"
          :class="{
            'is-invalid': errors?.poNumber,
          }"
          @input="removeSpaces"
        />
        <div v-if="errors.poNumber" class="invalid-feedback">
          {{ errors.poNumber }}
        </div>
      </div>
    </div>
    <div class="form-group" v-show="selectedDBCustomer === null && !isBillTo">
      <label for="street-address">Customer Name</label>
      <input
        type="text"
        id="street-address-line1"
        class="form-control"
        v-model.trim="customer.address.Name"
        :class="{
          'is-invalid': errors.AddressName,
        }"
      />
      <div v-if="errors.AddressName" class="invalid-feedback">
        {{ errors.AddressName }}
      </div>
    </div>
    <template v-if="!selectedDBCustomer && !isBillTo">
      <div class="form-group row">
        <div class="col-md-12">
          <label for="country-dropdown">Country: </label>
          <multiselect
            v-model="selectedCountry"
            deselect-label="Selected"
            :allow-empty="false"
            :options="countries"
            :close-on-select="true"
            :clear-on-select="false"
            placeholder="Select a Country"
            track-by="AlphaCode"
            label="Name"
            :searchable="true"
            @input="handleCountrySelect"
            :class="{
              'is-invalid': errors.Country,
              'invalid-dropwdown': errors.Country,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          >
          </multiselect>
          <div v-if="errors.Country" class="invalid-feedback">
            {{ errors.Country }}
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="street-address">Street Address</label>
        <input
          type="text"
          id="street-address-line1"
          class="form-control"
          v-model.trim="customer.address.Address1"
          :class="{
            'is-invalid': errors.Address1,
          }"
          :disabled="selectedDBCustomer != null || isBillTo"
        />
        <div v-if="errors.Address1" class="invalid-feedback">
          {{ errors.Address1 }}
        </div>
        <input
          type="text"
          id="street-address-line2"
          class="form-control mt-3"
          v-model.trim="customer.address.Address2"
          :disabled="selectedDBCustomer != null || isBillTo"
        />
      </div>
      <div class="form-group row">
        <div class="col-md-6" v-if="customer.address.Country === 'US'">
          <label for="state">State</label>
          <select
            id="state"
            class="form-control"
            v-model.trim="customer.address.State"
            :class="{
              'is-invalid': errors.State,
            }"
            :disabled="
              errors.Country === '' || selectedDBCustomer != null || isBillTo
            "
          >
            <option value="">Select Your State</option>
            <option
              v-for="state in states"
              :key="state.AlphaCode"
              :value="state.AlphaCode"
            >
              {{ state.Name }}
            </option>
          </select>
          <div v-if="errors.State" class="invalid-feedback">
            {{ errors.State }}
          </div>
        </div>
        <div class="col-md-6" v-else>
          <label for="street-address">State</label>
          <input
            type="text"
            id="state"
            class="form-control"
            v-model.trim="customer.address.State"
            :class="{
              'is-invalid': errors.State,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.State" class="invalid-feedback">
            {{ errors.State }}
          </div>
        </div>
        <div class="col-md-6">
          <label for="City">City</label>
          <input
            type="text"
            id="City"
            class="form-control"
            v-model.trim="customer.address.City"
            :class="{
              'is-invalid': errors.City,
            }"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="!customer.address.City" class="invalid-feedback">
            {{ errors.City }}
          </div>
        </div>
      </div>
      <div class="form-group row">
        <div class="col-md-6">
          <label for="zip-code">Zip/Postal Code</label>
          <input
            id="zip-code"
            class="form-control"
            v-model.trim="customer.address.Zip"
            :class="{
              'is-invalid': errors.Zip,
            }"
            placeholder=""
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.Zip" class="invalid-feedback">
            {{ errors.Zip }}
          </div>
        </div>
        <div class="col-md-6">
          <label for="Phone">Phone</label>
          <input
            type="tel"
            id="Phone"
            class="form-control"
            v-model.trim="customer.address.Phone"
            :class="{
              'is-invalid': errors.Phone,
            }"
            maxlength="10"
            placeholder="(000) 000-0000"
            @input="formatPhoneNumber"
            :disabled="selectedDBCustomer != null || isBillTo"
          />
          <div v-if="errors.Phone" class="invalid-feedback">
            {{ errors.Phone }}
          </div>
        </div>
      </div>
    </template>
    <div class="form-group row" v-if="showMultiplier">
      <!--div class="col-md-6">
        <label for="multiplier">Multiplier</label>
        <input
          type="number"
          id="multiplier"
          class="form-control"
          v-model.trim="customer.multiplier"
          disabled
        />
      </div-->
      <div class="col-md-12" v-if="showShippingMethod">
        <label for="shipping-method-dropdown">Shipping Method:</label>
        <select
          id="shipping-method-dropdown"
          class="form-control"
          v-model.trim="customer.shippingMethod"
        >
          <option
            v-for="method in shippingMethods"
            :key="method"
            :value="method"
          >
            {{ method }}
          </option>
        </select>
      </div>
    </div>
    <div class="form-check" v-show="showSameAddressCheckbox">
      <input
        type="checkbox"
        id="same-address"
        class="form-check-input mr-2"
        :value="sameAddress"
        @change="handleChangeCheckbox"
      />
      <label for="same-address" class="form-check-label"
        >My Billing and shipping address are the same</label
      >
    </div>
    <!-- <b-button variant="outline-primary float-right my-2" @click="submit"
      >Save</b-button
    > -->
  </div>
</template>
<script lang="ts">
import MxSite from '@/mixins/site'
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator'
import { ICountry, ICountryState } from '@/store/common/types'
import { IOrderCustomer } from '@/store/orders/types'
import _ from 'lodash'
import Multiselect from 'vue-multiselect'
import { blankCustomer, blankOrderAddress } from '@/store/orders/constants'
import { debounce } from 'typescript-debounce-decorator'
import { namespace } from 'vuex-class'

const nsInquiry = namespace('inquiries')

@Component({
  components: {
    Multiselect,
  },
})
export default class InquiryCustomerForm extends Mixins(MxSite) {
  @Prop({ type: String, required: true }) title!: string
  @Prop({ type: Boolean, default: false }) sameAddress!: boolean
  @Prop({ type: Boolean, default: false }) showSameAddressCheckbox!: boolean
  @Prop({ type: Boolean, default: false }) showMultiplier!: boolean
  @Prop({ type: Boolean, default: false }) showShippingMethod!: boolean
  @Prop({ type: Boolean, default: false }) showPOnumber!: boolean
  @Prop({ type: Boolean, default: true }) allowEmptyCustomer!: boolean
  @Prop({ type: Array, default: () => [] }) customers!: IOrderCustomer[]
  @Prop({ type: Boolean, default: false }) isBillTo!: boolean
  @Prop({ type: Array, default: () => [] }) countries!: ICountry[]
  @Prop({ type: Array, default: () => [] }) states!: ICountryState[]

  @nsInquiry.Action('setCurrentCustomer')
  private setCurrentCustomer: any

  public customer: IOrderCustomer = _.cloneDeep(blankCustomer)
  public shippingMethods = ['Best Way', 'UPS', 'FedEx', 'Will Call']
  public selectedCustomer = ''
  public selectedDBCustomer: any = null
  public searchKeyword = ''
  public selectedCountry: any = null
  public errors: any = {}
  public validated = false

  public async mounted() {
    if (this.customers.length === 1 && this.isBillTo) {
      this.selectedDBCustomer = this.customers[0]
      this.handleCustomerSelect(this.selectedDBCustomer).then(() =>
        this.validate(),
      )
    }
  }

  public creditAlert = false
  private validateCredit(customer: IOrderCustomer) {
    if (customer.isCreditBlocked === true) {
      this.errors.creditBlocked =
        'Your account has a credit hold which is blocking you from creating an inquiry. Please contact TPI Finance Office at 1-800-251-0382 x 337 for assistance.'
      return false
    }
    return true
  }

  public validate() {
    this.errors = {}
    if (!this.validateCredit(this.customer)) {
      if (!this.creditAlert) {
        this.toastWarning(this.errors.creditBlocked)
        this.creditAlert = true
        setTimeout(() => {
          this.creditAlert = false // Reset flag after a certain period
        }, 5000) // Reset after 5 seconds - prevents spamming of alerts
      }
    }
    if (this.isBillTo) {
      if (!this.customer.customerNo) {
        this.errors.customerNo = 'Customer is required'
      }
    } else {
      if (!this.customer.address.Name) {
        this.errors.AddressName = 'Address name is required'
      }
    }

    if (!this.customer.address.Name) {
      this.errors.Name = 'Name is required'
    }

    if (!this.selectedDBCustomer) {
      if (!this.customer.address.Address1) {
        this.errors.Address1 = 'Address1 is required'
      }

      if (!this.customer.address.State) {
        this.errors.State = 'State is required'
      }

      if (!this.customer.address.City) {
        this.errors.City = 'City is required'
      }

      if (!this.customer.address.Zip) {
        this.errors.Zip = 'Zip is required'
      }

      if (!this.customer.address.Phone) {
        this.errors.Phone = 'Phone is required'
      }

      if (!this.customer.address.Country) {
        this.errors.Country = 'Country is required'
      }
    }
    this.validated = true
    return Object.keys(this.errors).length === 0 ? this.customer : null
  }

  // Watchers
  @Watch('countries')
  onCountriesChanged(newCountries: ICountry[]) {
    this.selectedCountry = newCountries.find((c) => c.AlphaCode === 'US')
  }

  @Watch('customers')
  onCustomersChanged() {
    if (this.customers.length === 1 && this.isBillTo) {
      this.selectedDBCustomer = this.customers[0]
      this.handleCustomerSelect(this.selectedDBCustomer).then(() =>
        this.validate(),
      )
    }
  }

  @debounce(500)
  @Watch('customer', { deep: true })
  onCustomerChange() {
    if (this.validated) {
      this.validate()
    }
  }

  // Methods
  public removeSpaces() {
    this.customer.poNumber = this.customer.poNumber.replace(/\s/g, '')
  }

  public formatPhoneNumber() {
    const digitsOnly = this.customer.address.Phone.replace(/\D/g, '')
    this.customer.address.Phone = digitsOnly.replace(
      /^(\d{3})(\d{3})(\d{4})$/,
      '($1) $2-$3',
    )
  }

  public handleChangeCheckbox(event: any) {
    this.$emit('handleShowBillingAddress', event.target.checked)
  }

  public async handleCustomerSelect(customer: IOrderCustomer | null) {
    if (
      customer != null &&
      this.customer.customerNo !== customer.customerNo &&
      this.isBillTo
    ) {
      try {
        this.validateCredit(customer)
        await this.$store.dispatch(
          'order-state/fetchCustomerShipTos',
          customer.customerNo,
        )
        await this.$store.dispatch(
          'multiplier/fetchCustomerMultipliers',
          customer.customerNo,
        )
      } catch (error) {
        console.error('Error fetching customers:', error)
      }
    }
    if (customer != null) {
      this.customer = {
        ...blankCustomer,
        ...customer,
        poNumber: this.customer.poNumber,
        address: {
          ...customer?.address,
        },
      }
      this.selectedCountry = this.countries.find(
        (c) => c.AlphaCode === customer.address.Country,
      )
    } else {
      this.customer = { ...blankCustomer }
      this.selectedCountry = this.countries.find((c) => c.AlphaCode === 'US')
    }
    if (this.isBillTo) {
      this.setCurrentCustomer({
        customerNo: this.customer.customerNo,
        divisionCode: this.customer.divisionCode,
        divisionText: this.customer.divisionText,
      })
    }
  }

  public removeCustomer() {
    this.selectedDBCustomer = null
    this.customer = { ...blankCustomer }
    this.selectedCountry = this.countries.find((c) => c.AlphaCode === 'US')
  }

  public customLabel({
    companyName,
    customerNo,
  }: {
    companyName: string
    customerNo: string
  }) {
    return `${companyName} - ${customerNo}`
  }

  public async handleCountrySelect(country: { AlphaCode: any } | null) {
    if (country != null) {
      this.customer = {
        ...blankCustomer,
        ...this.customer,
        address: {
          ...blankOrderAddress,
          ...this.customer.address,
          Country: country.AlphaCode,
          State: '',
        },
      }
    } else {
      this.customer = {
        ...blankCustomer,
        ...this.customer,
        address: {
          ...this.customer.address,
          Country: '',
          State: '',
        },
      }
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.section1 label {
  font-weight: bold;
}

.section1 .form-check-label {
  font-weight: normal;
}

.invalid-dropwdown {
  border: 1px solid #cd0200;
  border-radius: 5px;
}

.bordered-container {
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  position: relative;
  padding: 15px;
}
</style>
